import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import type { DefaultError, QueryKey } from '@tanstack/query-core';
import {
  InfiniteData,
  keepPreviousData,
  Query,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { parseISO } from 'date-fns';

import {
  ArticleData,
  ArticleDisplayedStep,
  ArticleSteps,
  ArticleTaskType,
  BaseStepConfig,
  Endpoints,
  ExceptionalBrandCoverageReason,
  Permission,
  Weight,
} from '@/api';
import { ActivityOfType } from '@/components/activities/Activity';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { formatDate } from '@/utils/date';
import { useFetch } from '@/utils/fetch';
import { Currency } from '@/utils/number';

import { Medium } from './medium';
import { type ColorWithImage } from './product';
import {
  ArticleActionWithRelations,
  ArticleDefectWithRelations,
  ArticleWithRelations,
  ArticleWithRelationsFromRequestsList,
  formatRequestWithRelations,
  ProductAttributeValueWithParent,
  RequestWithRelations,
} from './request';
import { ArticleWithRelations as ArticleWithRelationsFromShipment } from './shipment';

export const isArchived = (article: ArticleWithRelations | ArticleWithRelationsFromRequestsList) =>
  !!article.archivedAt;

export const isActionMandatory = (action: ArticleActionWithRelations) =>
  !!action.articleActionToArticleDefects.some(({ mandatory }) => !!mandatory);

export const getEstimatedDueAtDate = (
  article: ArticleWithRelations | ArticleWithRelationsFromRequestsList
) => {
  if (article.archivedAt && article.archivalDetail?.type !== 'completed') {
    return null;
  }

  const stepsWithDueDate = article.steps?.filter((step) => !!step.dueDate);

  if (stepsWithDueDate?.at(-1)?.dueDate) {
    return new Date(stepsWithDueDate.at(-1)!.dueDate!);
  }

  return null;
};

export const getNumberOfDefects = (article: ArticleWithRelations) => {
  return article.snapshot.articleDefects.length;
};

export const hasDefects = (article: ArticleWithRelations) => {
  return getNumberOfDefects(article) > 0;
};

export const getNumberOfActions = (article: ArticleWithRelations) => {
  return article.snapshot.articleActions.length;
};

export const hasActions = (article: ArticleWithRelations) => {
  return getNumberOfActions(article) > 0;
};

export const hasQuoteRefusedByTheClient = (article: ArticleWithRelations) => {
  const latestValidationOrPaymentStep = article.steps?.findLast(
    (step) => (step.step === 'validation' || step.step === 'payment') && step.status !== 'pending'
  );

  if (latestValidationOrPaymentStep?.status === 'cancelled') {
    return true;
  }

  return false;
};

export const articleHasQuoteAnalysisRefused = (article: ArticleWithRelations) => {
  const latestAnalysisStep = article.steps?.findLast(
    (step) => step.step === 'analysis' && step.status !== 'pending'
  );

  if (latestAnalysisStep?.status === 'cancelled') {
    return true;
  }

  return false;
};

const hasRequiredCustomsInfo = (
  article: ArticleWithRelations | ArticleWithRelationsFromShipment
) => {
  const product = article.product;

  return (
    (product?.data.customsValue || article.data.customsValue) &&
    (product?.data.madein || article.data.madein) &&
    (product?.data.tariffCode || article.data.tariffCode) &&
    (product?.data.mid || article.data.mid)
  );
};

export const getCustomsValue = (article: ArticleWithRelations | ArticleWithRelationsFromShipment) =>
  article.product?.data.customsValue ?? article.data.customsValue;

export const getCustomsFile = (article: ArticleWithRelations | ArticleWithRelationsFromShipment) =>
  article.media?.find(({ type }) => type === 'customs-file');

const hasCustomsFile = (article: ArticleWithRelations | ArticleWithRelationsFromShipment) =>
  !!getCustomsFile(article);

export const isMissingCustomsData = (
  article: ArticleWithRelations | ArticleWithRelationsFromShipment,
  type: 'fields' | 'file'
) => (type === 'fields' ? !hasRequiredCustomsInfo(article) : !hasCustomsFile(article));

/**
 *
 * Check if a particular step might happen in the article workflow
 */
export const hasPotentialFutureStep = (
  article: ArticleWithRelations,
  step: BaseStepConfig['step']
) => {
  const currentArticleStepId = article.step?.id;

  const articleSteps = article.steps;

  if (!articleSteps?.length || !currentArticleStepId) {
    return false;
  }

  const articleCurrentStepIndex = articleSteps.findIndex(
    (articleStep) => articleStep.id === currentArticleStepId
  );

  const articleStepsFromCurrentStep = articleSteps.slice(articleCurrentStepIndex);

  const isStepPotentiallyIncomingForArticle = articleStepsFromCurrentStep.some(
    (articleStep) => articleStep.step === step
  );

  if (isStepPotentiallyIncomingForArticle) {
    return true;
  }
};

export const computeWarrantyExpirationDate = (
  article:
    | {
        activeWarrantyType: 'supplier';
        supplierWarrantyExpirationDate: string;
        hasDistributorWarrantyExtension: boolean;
        distributorWarrantyExpirationDate: string | null;
      }
    | { activeWarrantyType: 'distributor'; distributorWarrantyExpirationDate: string }
) => {
  if (article.activeWarrantyType === 'supplier') {
    const dateToConsider =
      article.hasDistributorWarrantyExtension && !!article.distributorWarrantyExpirationDate
        ? article.distributorWarrantyExpirationDate
        : article.supplierWarrantyExpirationDate;

    return formatDate(parseISO(dateToConsider), {
      dateStyle: 'long',
    });
  } else {
    return formatDate(parseISO(article.distributorWarrantyExpirationDate), {
      dateStyle: 'long',
    });
  }
};

export const getArticlePhoto = (article: { media?: Medium[] | undefined }) =>
  article.media?.find((medium) => medium.type === 'article-photo');

export const getArticlePhotoOrConditionPhoto = (article: {
  media?: Medium[];
}): Medium | undefined =>
  article.media?.find((m) => m.type === 'article-photo') ??
  article.media?.find((m) => m.type === 'condition-photo');

export const getArticleDisplayPhoto = (article: {
  color: I18nWording | null;
  media?: Medium[];
  product: {
    colorsWithImage?: ColorWithImage[] | null;
    data?: { image?: string };
  } | null;
}): { url: string } | undefined => {
  const productPicture = getProductPictureFromArticleColor({ article });

  return (
    getArticlePhotoOrConditionPhoto(article) ??
    (productPicture ? { url: productPicture } : undefined)
  );
};

export type GetArticleNameProps = {
  article:
    | {
        product: { name: string } | null;
        color: I18nWording | null;
        productAttributeValues: ProductAttributeValueWithParent[];
      }
    | undefined;
  type?: 'short' | 'full';
};

export const useGetArticleName = () => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const unnamedTranslation = t({ id: 'article.name.unnamed', message: 'Unnamed item' });

  return ({ article }: GetArticleNameProps) => {
    if (article?.product) {
      return article.product.name;
    }

    if (article?.productAttributeValues) {
      return getArticleNameFromProductAttributeValues(article, _db, unnamedTranslation);
    }

    return unnamedTranslation;
  };
};

const getArticleNameFromProductAttributeValues = (
  article: GetArticleNameProps['article'],
  _db: (wording: I18nWording) => string,
  unamedTranslation: string
) => {
  if (!article?.productAttributeValues) {
    return unamedTranslation;
  }

  const firstProductAttributeValue = article.productAttributeValues.find(
    (productAttributeValue) =>
      productAttributeValue.productAttribute.parentProductAttributeValueId === null
  );

  const secondProductAttributeValue = article.productAttributeValues.find(
    (productAttributeValue) =>
      productAttributeValue.productAttribute.parentProductAttributeValueId ===
      firstProductAttributeValue?.id
  );

  const secondProductAttributeString = secondProductAttributeValue
    ? _db(secondProductAttributeValue.name)
    : undefined;

  if (!secondProductAttributeString) {
    return unamedTranslation;
  }

  const colorString = article?.color ? _db(article.color) : undefined;

  const thirdProductAttributeValue = article.productAttributeValues.find(
    (productAttributeValue) =>
      productAttributeValue.productAttribute.parentProductAttributeValueId ===
      secondProductAttributeValue?.id
  );
  const thirdProductAttributeString = thirdProductAttributeValue
    ? _db(thirdProductAttributeValue.name)
    : undefined;

  if (thirdProductAttributeString && colorString) {
    return `${secondProductAttributeString} (${thirdProductAttributeString}, ${colorString})`;
  } else if (thirdProductAttributeString || colorString) {
    return `${secondProductAttributeString} (${thirdProductAttributeString ?? colorString})`;
  } else {
    return secondProductAttributeString;
  }
};

export const getProductPictureFromArticleColor = ({
  article,
  articleExchange,
}: {
  article?: {
    color: I18nWording | null;
    product: {
      colorsWithImage?: ColorWithImage[] | null;
      data?: { image?: string };
    } | null;
  };
  articleExchange?: {
    replacementColor?: I18nWording | null;
    replacementProduct?: {
      colorsWithImage?: ColorWithImage[] | null;
      data?: { image?: string };
    } | null;
  } | null;
}): string | null => {
  if (article) {
    const productColorWithImage = article.product?.colorsWithImage?.find(
      (colorWithImage) => colorWithImage.color.en === article.color?.en
    );
    const defaultProductPicture = article.product?.data?.image;

    return productColorWithImage?.image ?? defaultProductPicture ?? null;
  }

  if (articleExchange) {
    const replacementProductColorWithImage =
      articleExchange.replacementProduct?.colorsWithImage?.find(
        (colorWithImage) => colorWithImage.color.en === articleExchange.replacementColor?.en
      );
    const defaultProductPicture = articleExchange.replacementProduct?.data?.image;

    return replacementProductColorWithImage?.image ?? defaultProductPicture ?? null;
  }

  return null;
};

export const useHasActionAlreadyBeenRefused = (articleId: string) => {
  const { data: { activities: refusedByClientActivities } = { activities: [] } } = useActivities({
    articleId,
    types: ['article_action_refused_by_client'],
  });

  const refusedActionTypeIds = new Set(
    refusedByClientActivities.flatMap((activity) => {
      const actionTypeId = activity.data?.action?.actionTypeId;
      const packActionTypeId = activity.data?.action?.packActionTypeId;

      return (actionTypeId || packActionTypeId) as string;
    })
  );

  return (actionTypeId: string) => refusedActionTypeIds.has(actionTypeId);
};

export const useArticleName = (props: GetArticleNameProps) => {
  const getArticleName = useGetArticleName();

  return getArticleName(props);
};

export const ARTICLE_DISPLAYED_STEPS = [
  {
    id: 'service-choice',
    name: msg({ id: 'article.step.service-choice', message: 'Diagnosis' }),
  },
  {
    id: 'validation',
    name: msg({ id: 'article.step.validation', message: 'Validation' }),
  },
  {
    id: 'transit',
    name: msg({ id: 'article.step.transit', message: 'Transit' }),
  },
  {
    id: 'analysis',
    name: msg({ id: 'article.step.analysis', message: 'Analysis' }),
  },
  {
    id: 'payment',
    name: msg({ id: 'article.step.payment', message: 'Payment' }),
  },
  {
    id: 'refund',
    name: msg({ id: 'article.step.refund', message: 'Refund' }),
  },
  {
    id: 'repair',
    name: msg({ id: 'article.step.repair', message: 'Repair' }),
  },
  {
    id: 'exchange',
    name: msg({ id: 'article.step.exchange', message: 'Exchange' }),
  },
  {
    id: 'quality-control',
    name: msg({ id: 'article.step.quality-control', message: 'Quality control' }),
  },
  {
    id: 'processing',
    name: msg({ id: 'article.step.processing', message: 'Processing' }),
  },
  {
    id: 'delivery',
    name: msg({ id: 'article.step.delivery', message: 'Delivery' }),
  },
  {
    id: 'client-pickup',
    name: msg({ id: 'article.step.client-pickup', message: 'Client pickup' }),
  },
  {
    id: 'in-recycling',
    name: msg({ id: 'article.step.in-recycling', message: 'In recycling' }),
  },
  {
    id: 'archived',
    name: msg({ id: 'article.step.archived', message: 'Archived' }),
  },
  {
    id: 'completed',
    name: msg({ id: 'article.step.completed', message: 'Completed' }),
  },
] satisfies { id: ArticleDisplayedStep; name: MessageDescriptor }[];

export const getDisplayedStep = (step: BaseStepConfig | null): ArticleDisplayedStep | null => {
  if (!step) {
    return null;
  }

  if (step.step === 'creation') {
    return null;
  }

  if (step.step === 'archival') {
    return 'completed';
  }

  if (
    step.step === 'transit' &&
    step.config.destinationType === 'client' &&
    step.config.clientPickup
  ) {
    return 'client-pickup';
  }

  if (
    step.step === 'transit' &&
    ((step.config.destinationType === 'client' && !step.config.clientPickup) ||
      (step.config.originType === 'workshop' && step.config.destinationType === 'store'))
  ) {
    return 'delivery';
  }

  return step.step;
};

export const ARTICLE_SERVICE_TYPES = [
  {
    id: 'care-repair',
    label: msg({ id: 'article.service-type.care-repair', message: 'Care and repair' }),
  },
  {
    id: 'exchange',
    label: msg({ id: 'article.service-type.exchange', message: 'Exchange' }),
  },
  {
    id: 'exchange-archival',
    label: msg({ id: 'article.service-type.exchange-archival', message: 'Exchange archival' }),
  },
  {
    id: 'refund',
    label: msg({ id: 'article.service-type.refund', message: 'Refund' }),
  },
  {
    id: 'none',
    label: msg({ id: 'article.service-type.none', message: 'None' }),
  },
] as const;

export type ArticleServiceType = (typeof ARTICLE_SERVICE_TYPES)[number]['id'];

export const ARTICLE_WARRANTY_TYPES = [
  {
    id: 'applicable',
    label: msg({ id: 'requests.filters.warranty-type.applicable', message: 'Applicable' }),
  },
  {
    id: 'applicable-without-extension',
    label: msg({
      id: 'requests.filters.warranty-type.applicable-without-extension',
      message: 'Applicable (no extension)',
    }),
  },
  {
    id: 'applicable-with-extension',
    label: msg({
      id: 'requests.filters.warranty-type.applicable-with-extension',
      message: 'Applicable (extension)',
    }),
  },
  {
    id: 'not-applicable',
    label: msg({ id: 'requests.filters.warranty-type.not-applicable', message: 'Not applicable' }),
  },
] as const;

export type ArticleWarrantyType = (typeof ARTICLE_WARRANTY_TYPES)[number]['id'];

export const CLIENT_ARTICLE_CANCELLATION_REASONS = [
  {
    id: 'too-expensive',
    label: msg({
      id: 'article.archival.reason.too-expensive',
      message: 'The quote is too expensive',
    }),
    onBehalfLabel: msg({
      id: 'article.archival.reason.too-expensive.on-behalf',
      message: 'The price is too expensive',
    }),
  },
  {
    id: 'no-longer-want-repair',
    label: msg({
      id: 'article.archival.reason.no-longer-want-repair',
      message: 'I no longer wish to have it repaired',
    }),
    onBehalfLabel: msg({
      id: 'article.archival.reason.no-longer-want-repair.on-behalf',
      message: 'The client no longer wishes to have their item repaired',
    }),
  },
  {
    id: 'found-another-solution',
    label: msg({
      id: 'article.archival.reason.found-another-solution',
      message: 'I found another solution',
    }),
    onBehalfLabel: msg({
      id: 'article.archival.reason.found-another-solution.on-behalf',
      message: 'The client found another solution',
    }),
  },
  {
    id: 'do-not-wish-to-answer',
    label: msg({
      id: 'article.archival.reason.do-not-wish-to-answer',
      message: 'I do not wish to answer',
    }),
    onBehalfLabel: msg({
      id: 'article.archival.reason.do-not-wish-to-answer.on-behalf',
      message: 'The client prefers not to answer',
    }),
  },
  {
    id: 'other',
    label: msg({ id: 'article.archival.reason.other', message: 'Other reason, please specify' }),
  },
] as const;

type ClientArticleCancellationReason = (typeof CLIENT_ARTICLE_CANCELLATION_REASONS)[number]['id'];

export const STORE_ARTICLE_CANCELLATION_REASONS = [
  {
    id: 'store-too-expensive',
    label: msg({
      id: 'article.archival.reason.store-too-expensive',
      message: 'The quote is too expensive',
    }),
  },
  {
    id: 'store-do-not-wish-to-answer',
    label: msg({
      id: 'article.archival.reason.store-do-not-wish-to-answer',
      message: 'I prefer not to answer',
    }),
  },
  {
    id: 'store-other',
    label: msg({
      id: 'article.archival.reason.store-other',
      message: 'Other reason, please specify',
    }),
  },
] as const;

type StoreArticleCancellationReason = (typeof STORE_ARTICLE_CANCELLATION_REASONS)[number]['id'];

export const ARTICLE_CANCELLATION_REASONS = [
  ...CLIENT_ARTICLE_CANCELLATION_REASONS,
  ...STORE_ARTICLE_CANCELLATION_REASONS,
] as const;

export type ArticleCancellationReason =
  ClientArticleCancellationReason | StoreArticleCancellationReason;

export const ARTICLE_CANCELLATION_TYPES = [
  {
    id: 'requestor_refusal',
    name: msg({
      id: 'article.cancellation-type.requestor_refusal',
      message: 'Client refusal',
    }),
    color: 'black',
  },
] as const;

type ArticleCancellationType = (typeof ARTICLE_CANCELLATION_TYPES)[number]['id'];

export const ARTICLE_ARCHIVAL_TYPES = [
  {
    id: 'manual',
    name: msg({ id: 'article.archival-type.manual', message: 'Archived' }),
    color: 'red',
  },
  {
    id: 'automatic',
    name: msg({ id: 'article.archival-type.automatic', message: 'Archived' }),
    color: 'red',
  },
  {
    id: 'in-recycling',
    name: msg({ id: 'article.archival-type.in-recycling', message: 'In recycling' }),
    color: 'green',
  },
  {
    id: 'completed',
    name: msg({ id: 'article.archival-type.completed', message: 'Completed' }),
    color: 'green',
  },
] as const;

export type ArticleCancellationDetail = {
  type: ArticleCancellationType;
  reason?: ArticleCancellationReason;
  otherReason?: string;
};

export const someActionsDontHaveWorkshopAndClientPrices = ({
  article,
  request,
}: {
  article: ArticleWithRelations;
  request: RequestWithRelations;
}) => {
  const someActionsDontHaveWorkshopPrice = !article.snapshot.articleActions.every(
    (action) =>
      !!action.workshopPrice?.amountPerEntity?.find(
        (amount) => amount.entityId === article.workshopId
      ) &&
      action.spareParts.every(
        (sparePart) =>
          !!sparePart.workshopPrice?.amountPerEntity?.find(
            (amount) => amount.entityId === article.workshopId
          )
      )
  );

  const someActionsDontHaveClientPrice =
    !!request.client &&
    !article.snapshot.articleActions.every(
      (action) =>
        !!action.organizationPrice?.amountPerEntity?.[0] &&
        action.spareParts.every((sparePart) => !!sparePart.organizationPrice?.amountPerEntity?.[0])
    );

  return someActionsDontHaveWorkshopPrice || someActionsDontHaveClientPrice;
};

export const useShipmentArticles = (
  params: Endpoints['GET /shipments/articles']['query'],
  options?: {
    keepPreviousData?: boolean;
    enabled?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /shipments/articles']>();

  return useQuery({
    queryKey: ['shipments/articles', params],
    queryFn: () => fetch('/shipments/articles', params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled,
  });
};

export const TEMPORARY_ID_PREFIX = 'tmp-';

export const useCreateAction = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:articleId/actions']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: ({
      actionTypeOrganization,
      packActionTypeOrganization,
      ...body
    }: Omit<
      Endpoints['POST /articles/:articleId/actions']['body'],
      'actionTypeOrganizationId' | 'packActionTypeOrganizationId'
    > & {
      actionTypeOrganization?: ArticleActionWithRelations['actionTypeOrganization'];
      packActionTypeOrganization?: ArticleActionWithRelations['packActionTypeOrganization'];
    }) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleActions: [
                      ...article.snapshot.articleActions,
                      {
                        ...body,
                        id: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                        stableId: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                        brandResponsibility: body.brandResponsibility ?? false,
                        context: body.context ?? null,
                        createdAt: new Date().toISOString(),
                        actionTypeOrganizationId: actionTypeOrganization?.id ?? null,
                        actionTypeOrganization: actionTypeOrganization ?? null,
                        packActionTypeOrganizationId: packActionTypeOrganization?.id ?? null,
                        packActionTypeOrganization: packActionTypeOrganization ?? null,
                        additionalInfo: body.additionalInfo ?? null,
                        priceRefashionBonusDetail: [],
                        customDescription: body.description ?? null,
                        customOrganizationPrice: null,
                        customWorkshopPrice: null,
                        catalogOrganizationPrice: null,
                        catalogWorkshopPrice: null,
                        articleDefects: [],
                        articleActionToArticleDefects: [],
                        parents: [],
                        children: [],
                        spareParts: [],
                        doneByWorkshop: null,
                        workshopWarrantyExpirationDate: null,
                      },
                    ],
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return fetch(`/articles/${articleId}/actions`, undefined, {
        method: 'POST',
        body: {
          ...body,
          actionTypeOrganizationId: actionTypeOrganization?.id,
          packActionTypeOrganizationId: packActionTypeOrganization?.id,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};

export const useUpdateAction = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:articleId/actions/:actionId']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: ({
      actionId,
      body,
    }: {
      actionId: string;
      body: Endpoints['PATCH /articles/:articleId/actions/:actionId']['body'];
    }) =>
      fetch(`/articles/${articleId}/actions/${actionId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onMutate: ({
      actionId,
      body,
    }: {
      actionId: string;
      body: Endpoints['PATCH /articles/:articleId/actions/:actionId']['body'];
    }) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleActions: article.snapshot.articleActions.map((action) =>
                      action.id === actionId
                        ? {
                            ...action,
                            ...body,
                            customDescription: body.description ?? action.customDescription,
                            customOrganizationPrice: body.customOrganizationPrice
                              ? null
                              : action.customOrganizationPrice,
                            customWorkshopPrice: body.customWorkshopPrice
                              ? null
                              : action.customWorkshopPrice,
                            articleActionToArticleDefects:
                              body.defectIds?.map((id) => ({
                                articleDefectId: id,
                                articleDefect: {
                                  id,
                                  stableId: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                                },
                                mandatory: false,
                              })) ?? action.articleActionToArticleDefects,
                          }
                        : action
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return { currentRequest };
    },
    onError: (_err, _variables, context) => {
      if (context?.currentRequest) {
        queryClient.setQueryData(
          requestQueryKey,
          formatRequestWithRelations(context.currentRequest)
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: requestQueryKey });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId, 'deposit-options'] });
    },
  });
};

export const useDeleteAction = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /articles/:articleId/actions/:actionId']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: (actionId: string) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleActions: article.snapshot.articleActions.filter(
                      (action) => action.id !== actionId
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return fetch(`/articles/${articleId}/actions/${actionId}`, undefined, {
        method: 'DELETE',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};

export const useCreateDefect = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:articleId/defects']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: ({
      defectTypeOrganization,
      ...body
    }: Omit<Endpoints['POST /articles/:articleId/defects']['body'], 'defectTypeOrganizationId'> & {
      defectTypeOrganization?: ArticleDefectWithRelations['defectTypeOrganization'];
    }) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleDefects: [
                      ...article.snapshot.articleDefects,
                      {
                        ...body,
                        id: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                        stableId: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
                        toBeRepaired: body.toBeRepaired ?? true,
                        context: body.context ?? null,
                        createdAt: new Date().toISOString(),
                        defectTypeOrganizationId: defectTypeOrganization?.id ?? null,
                        defectTypeOrganization: defectTypeOrganization ?? null,
                        customDescription: body.description ?? null,
                        brandResponsibility: false,
                        removesAllBrandResponsibility: false,
                        defaultBrandResponsibility: false,
                        exceptionalBrandResponsibilityCoverageRequested: false,
                        exceptionalBrandResponsibilityCoverageReason: null,
                        exceptionalBrandResponsibilityCoverageOriginalRequestId: null,
                        exceptionalBrandResponsibilityCoverageOriginalRequest: null,
                        articleConditionId: null,
                        addedByOrganizationId: null,
                        addedByWorkshopId: null,
                        articleActions: [],
                        articleActionToArticleDefects: [],
                      },
                    ],
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return fetch(`/articles/${articleId}/defects`, undefined, {
        method: 'POST',
        body: {
          ...body,
          defectTypeOrganizationId: defectTypeOrganization?.id,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};

export type WorkshopWarrantyReturnRequest =
  Endpoints['GET /articles/:articleId/defects/:defectId/workshop-warranty-return-requests']['response']['requests'][number];

export const useWorkshopWarrantyReturnRequests = (
  {
    articleId,
    defectId,
    ...params
  }: {
    articleId: string;
    defectId: string;
  } & Endpoints['GET /articles/:articleId/defects/:defectId/workshop-warranty-return-requests']['query'],
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch =
    useFetch<
      Endpoints['GET /articles/:articleId/defects/:defectId/workshop-warranty-return-requests']
    >();

  return useQuery({
    queryKey: [
      'articles',
      articleId,
      'defects',
      defectId,
      'workshop-warranty-return-requests',
      params,
    ],
    queryFn: () =>
      fetch(`/articles/${articleId}/defects/${defectId}/workshop-warranty-return-requests`, params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useUpdateDefect = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:articleId/defects/:defectId']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: ({
      defectId,
      body,
    }: {
      defectId: string;
      body: Endpoints['PATCH /articles/:articleId/defects/:defectId']['body'];
    }) =>
      fetch(`/articles/${articleId}/defects/${defectId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onMutate: ({
      defectId,
      body,
    }: {
      defectId: string;
      body: Endpoints['PATCH /articles/:articleId/defects/:defectId']['body'];
    }) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleDefects: article.snapshot.articleDefects.map((defect) =>
                      defect.id === defectId
                        ? {
                            ...defect,
                            ...body,
                            customDescription: body.description ?? defect.customDescription ?? null,
                          }
                        : defect
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return { currentRequest };
    },
    onError: (_err, _variables, context) => {
      if (context?.currentRequest) {
        queryClient.setQueryData(
          requestQueryKey,
          formatRequestWithRelations(context.currentRequest)
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: requestQueryKey });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useDeleteDefect = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /articles/:articleId/defects/:defectId']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: (defectId: string) => {
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = {
          ...currentRequest,
          articles: currentRequest.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    articleDefects: article.snapshot.articleDefects.filter(
                      (defect) => defect.id !== defectId
                    ),
                  },
                }
              : article
          ),
        };

        queryClient.setQueryData(requestQueryKey, formatRequestWithRelations(updatedRequest));
      }

      return fetch(`/articles/${articleId}/defects/${defectId}`, undefined, {
        method: 'DELETE',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};

export const useUpdateArticleSubLocation = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:id/location']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationKey: ['articles', articleId, 'location'],
    mutationFn: ({ subLocation }: { subLocation: string | null }) =>
      fetch(`/articles/${articleId}/location`, undefined, {
        method: 'PATCH',
        body: { subLocation },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: requestQueryKey });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useUpdateArticle = (
  {
    articleId,
    requestId,
    shipmentId,
  }: {
    articleId?: string;
    requestId: string;
    shipmentId?: string;
  },
  {
    optimistic = true,
  }: {
    optimistic?: boolean;
  } = {}
) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:id']>();

  const requestQueryKey = ['requests', requestId];
  const shipmentQueryKey = ['shipments', shipmentId];

  return useMutation({
    mutationKey: ['articles', articleId],
    mutationFn: ({
      articleId: dynamicArticleId,
      data,
      optimisticData,
    }: {
      articleId?: string;
      data: {
        productId?: string | null;
        uniqueArticleId?: string | null;
        purchaseDate?: string | null;
        purchaseLocation?: string | null;
        purchaseCountry?: string | null;
        purchaseChannel?: I18nWording | null;
        hasDistributorWarrantyExtension?: boolean;
        warrantyOverride?: boolean;
        warrantyOverrideReason?: 'commercial-gesture' | 'warranty-verified-manually' | null;
        orderNumber?: string | null;
        costManualDiscount?: number | null;
        priceManualDiscount?: number | null;
        manualClientThresholdAmount?: number | null;
        manualOrganizationThresholdAmount?: number | null;
        clientRequireQuote?: boolean;
        organizationRequireQuote?: boolean;
        brandId?: string | null;
        otherBrandName?: string | null;
        color?: I18nWording | null;
        size?: I18nWording | null;
        weight?: Weight | null;
        data?: ArticleData;
        clientComment?: string | null;
        requalificationWorkshopComment?: string | null;
        requalificationAfterSalesComment?: string | null;
        productAttributeValues?: ProductAttributeValueWithParent[];
        proofOfPurchaseIds?: string[];
        defectPhotoIds?: string[];
      };
      optimisticData?: Partial<ArticleWithRelations>;
    }) => {
      const finalArticleId = dynamicArticleId ?? articleId;
      const body = {
        ...data,
        productAttributeValues: data.productAttributeValues?.map(({ id }) => id),
      };

      /* Optimistic update */
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest && optimistic) {
        const newRequest = formatRequestWithRelations({
          ...currentRequest,
          articles: currentRequest?.articles.map((article) =>
            article.id === finalArticleId ? { ...article, ...data, ...optimisticData } : article
          ),
        });

        queryClient.setQueryData(requestQueryKey, newRequest);
      }

      return fetch(`/articles/${finalArticleId}`, undefined, {
        method: 'PATCH',
        body,
      });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: requestQueryKey });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['articles', articleId] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });

      if (!!variables.data?.proofOfPurchaseIds || !!variables.data?.defectPhotoIds) {
        queryClient.invalidateQueries({ queryKey: ['media'] });
      }

      if (shipmentId) {
        queryClient.invalidateQueries({ queryKey: shipmentQueryKey });
      }
    },
  });
};

export const useResetDefect = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:articleId/defects/reset']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: (defectStableId: string) =>
      fetch(`/articles/${articleId}/defects/reset`, undefined, {
        method: 'POST',
        body: { stableId: defectStableId },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: requestQueryKey });
    },
  });
};

export const useResetAction = ({
  articleId,
  requestId,
}: {
  articleId: string;
  requestId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:articleId/actions/reset']>();
  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationFn: (actionStableId: string) =>
      fetch(`/articles/${articleId}/actions/reset`, undefined, {
        method: 'POST',
        body: { stableId: actionStableId },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: requestQueryKey });
    },
  });
};

export const useDeleteArticle = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch();

  return useMutation({
    mutationFn: (id: string) => fetch(`/articles/${id}`, undefined, { method: 'DELETE' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['requests'] }),
  });
};

export type ArticleActivity =
  Endpoints['GET /articles/:id/activities']['response']['activities'][number];

type ArticleActivityType = ArticleActivity['type'];

export type ArticleActivityOfType<T extends ArticleActivity['type']> = Extract<
  ArticleActivity,
  { type: T }
>;

type UseActivitiesParams<T extends ArticleActivityType> = {
  limit?: number;
  types?: T[];
};
type UseActivitiesData<T extends ArticleActivityType> = {
  activities: ArticleActivityOfType<T>[];
  meta: Endpoints['GET /articles/:id/activities']['response']['meta'];
};

export const useActivities = <T extends ArticleActivity['type']>(
  {
    articleId,
    ...params
  }: {
    articleId: string;
    limit?: number;
    types?: T[];
  },
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<
            UseActivitiesData<T>,
            Error,
            UseActivitiesData<T>,
            (string | UseActivitiesParams<T>)[]
          >
        ) => number | false | undefined);
  }
) => {
  const fetch = useFetch<Endpoints['GET /articles/:id/activities']>();

  return useQuery({
    queryKey: ['activities', articleId, params],
    queryFn: () =>
      fetch<{
        activities: ArticleActivityOfType<T>[];
        meta: Endpoints['GET /articles/:id/activities']['response']['meta'];
      }>(`/articles/${articleId}/activities`, params),
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
  });
};

export const useInfiniteActivities = ({ articleId }: { articleId: string }) => {
  const fetch = useFetch<Endpoints['GET /articles/:id/activities']>();

  return useInfiniteQuery<
    Endpoints['GET /articles/:id/activities']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /articles/:id/activities']['response']>,
    QueryKey,
    string | null
  >({
    queryFn: ({ pageParam }) => {
      const requestParams = pageParam ? { before: pageParam } : undefined;

      return fetch(`/articles/${articleId}/activities`, requestParams);
    },
    queryKey: ['activities', articleId],
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.meta.next,
  });
};

export const useUpdateServiceChoice = ({
  requestId,
  articleId,
}: {
  requestId: string;
  articleId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:id/service-choice']>();

  const requestQueryKey = ['requests', requestId];

  return useMutation({
    mutationKey: ['articles', articleId, 'service-choice'],
    mutationFn: ({
      serviceChoice,
      workshopId,
    }: Endpoints['PATCH /articles/:id/service-choice']['body']) => {
      /* Optimistic update */
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const newRequest = formatRequestWithRelations({
          ...currentRequest,
          articles: currentRequest?.articles.map((article) =>
            article.id === articleId
              ? {
                  ...article,
                  snapshot: {
                    ...article.snapshot,
                    serviceChoice: serviceChoice ?? article.snapshot.serviceChoice,
                  },
                  workshopId: workshopId ?? article.workshopId,
                }
              : article
          ),
        });

        queryClient.setQueryData(requestQueryKey, newRequest);
      }

      return fetch(`/articles/${articleId}/service-choice`, undefined, {
        method: 'PATCH',
        body: {
          serviceChoice,
          workshopId,
        },
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: requestQueryKey });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};

export const useIsUpdatingWorkshop = (articleId: string) => {
  const pendingUpdateServiceChoiceMutations = useMutationState({
    filters: {
      mutationKey: ['articles', articleId, 'service-choice'],
      exact: true,
      status: 'pending',
    },
  });

  const isUpdatingWorkshop = !!pendingUpdateServiceChoiceMutations.some(
    (mutation) =>
      (mutation.variables as Endpoints['PATCH /articles/:id/service-choice']['body'])?.workshopId
  );

  return isUpdatingWorkshop;
};

export const useIsUpdatingServiceChoiceOrWorkshop = (articleId: string) => {
  const pendingUpdateServiceChoiceMutations = useMutationState({
    filters: {
      mutationKey: ['articles', articleId, 'service-choice'],
      exact: true,
      status: 'pending',
    },
  });

  const isUpdatingServiceChoiceOrWorkshop = !!pendingUpdateServiceChoiceMutations.some(
    (mutation) =>
      (mutation.variables as Endpoints['PATCH /articles/:id/service-choice']['body'])
        ?.serviceChoice ||
      (mutation.variables as Endpoints['PATCH /articles/:id/service-choice']['body'])?.workshopId
  );

  return isUpdatingServiceChoiceOrWorkshop;
};

export const useValidateServiceChoice = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/validate-service-choice']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/validate-service-choice`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useAcceptDispatch = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/accept-dispatch']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/accept-dispatch`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useValidateCustoms = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/validate-customs']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/validate-customs`, undefined, { method: 'POST' }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useSubmitDispatchProposal = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/submit-dispatch-proposal']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/submit-dispatch-proposal`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useRefuseDispatch = ({ article }: { article: { id: string; requestId: string } }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/refuse-dispatch']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/refuse-dispatch']['body']) =>
      fetch(`/articles/${article.id}/refuse-dispatch`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.removeQueries({ queryKey: ['requests', article.requestId] });
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useAnalyze = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/analyze']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/analyze']['body']) =>
      fetch(`/articles/${articleId}/analyze`, undefined, { method: 'POST', body }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useAnalysisValidations = ({ articleId }: { articleId: string }) => {
  const fetch = useFetch<Endpoints['GET /articles/:id/analysis-validations']>();

  return useQuery({
    queryKey: ['articles', articleId, 'analysis-validations'],
    queryFn: () => fetch(`/articles/${articleId}/analysis-validations`),
    gcTime: 0,
  });
};

export const useStartRepair = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/start-repair']>();

  return useMutation({
    mutationFn: () => fetch(`/articles/${articleId}/start-repair`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useStartExchange = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/start-exchange']>();

  return useMutation({
    mutationFn: () => fetch(`/articles/${articleId}/start-exchange`, undefined, { method: 'POST' }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useRepair = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/repair']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/repair']['body']) =>
      fetch(`/articles/${articleId}/repair`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({
        queryKey: ['articles', articleId, 'condition-form', 'quality_control'],
      });
    },
  });
};

export const useRefuseAnalysis = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/refuse-analysis']>();

  return useMutation({
    mutationFn: (data: Endpoints['POST /articles/:id/refuse-analysis']['body']) =>
      fetch(`/articles/${articleId}/refuse-analysis`, undefined, { method: 'POST', body: data }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useExitRepair = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/exit-repair']>();

  return useMutation({
    mutationFn: (data: Endpoints['POST /articles/:id/exit-repair']['body']) =>
      fetch(`/articles/${articleId}/exit-repair`, undefined, { method: 'POST', body: data }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useDiscardRequalification = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/discard-requalification']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/discard-requalification`, undefined, { method: 'POST' }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useRefuseRequalification = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/refuse-requalification']>();

  return useMutation({
    mutationFn: (data: Endpoints['POST /articles/:id/refuse-requalification']['body']) =>
      fetch(`/articles/${articleId}/refuse-requalification`, undefined, {
        method: 'POST',
        body: data,
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useValidateRequalification = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/validate-requalification']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/validate-requalification`, undefined, {
        method: 'POST',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useCompleteClientPickup = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/complete-client-pickup']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/complete-client-pickup`, undefined, {
        method: 'POST',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useValidateTransit = ({ articleId }: { articleId?: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/validate-transit']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/validate-transit']['body']) =>
      fetch(`/articles/${articleId}/validate-transit`, undefined, {
        method: 'POST',
        body: {
          trackingNumber: body?.trackingNumber,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useStartSelfHandledTransit = ({ articleId }: { articleId?: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/start-self-handled-transit']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/start-self-handled-transit']['body']) =>
      fetch(`/articles/${articleId}/start-self-handled-transit`, undefined, {
        method: 'POST',
        body: {
          trackingNumber: body?.trackingNumber,
        },
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useCompleteSelfHandledTransit = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/complete-self-handled-transit']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/complete-self-handled-transit`, undefined, {
        method: 'POST',
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useValidateDispatchProposal = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/validate-dispatch-proposal']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/validate-dispatch-proposal`, undefined, {
        method: 'POST',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useRefuseDispatchProposal = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/refuse-dispatch-proposal']>();

  return useMutation({
    mutationFn: (reason: string) =>
      fetch(`/articles/${articleId}/refuse-dispatch-proposal`, undefined, {
        method: 'POST',
        body: {
          reason,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

// The client threshold applies to the organizationPrice
// The organization threshold applies to the workshopPrice
export type ArticleThresholdType = 'client' | 'organization';

export const useResetThreshold = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/reset-threshold']>();

  return useMutation({
    mutationFn: (thresholdType: ArticleThresholdType) =>
      fetch(`/articles/${articleId}/reset-threshold`, undefined, {
        method: 'POST',
        body: {
          thresholdType,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useSetValidationChoice = ({ requestId }: { requestId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:id/validation-choice']>();

  return useMutation({
    mutationFn: ({
      articleId,
      choice,
    }: {
      articleId: string;
      choice: Endpoints['PATCH /articles/:id/validation-choice']['body'];
    }) =>
      fetch(`/articles/${articleId}/validation-choice`, undefined, {
        method: 'PATCH',
        body: choice,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({
        queryKey: ['request', requestId, 'client', 'shipping-options'],
      });
    },
  });
};

export const useValidateOnBehalfOfClient = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/validate-on-behalf-of-client']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/validate-on-behalf-of-client']['body']) =>
      fetch(`/articles/${articleId}/validate-on-behalf-of-client`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useCompleteQualityControl = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/complete-quality-control']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/complete-quality-control']['body']) =>
      fetch(`/articles/${articleId}/complete-quality-control`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['articles', articleId, 'condition-form'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useStartQualityControlServiceAdjustment = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch =
    useFetch<Endpoints['POST /articles/:id/start-quality-control-service-adjustment']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/start-quality-control-service-adjustment`, undefined, {
        method: 'POST',
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['articles', articleId, 'condition-form'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useCompleteQualityControlServiceAdjustment = ({
  articleId,
}: {
  articleId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch =
    useFetch<Endpoints['POST /articles/:id/complete-quality-control-service-adjustment']>();

  return useMutation({
    mutationFn: (
      body: Endpoints['POST /articles/:id/complete-quality-control-service-adjustment']['body']
    ) =>
      fetch(`/articles/${articleId}/complete-quality-control-service-adjustment`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['articles', articleId, 'condition-form'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useCompleteProcessing = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/complete-processing']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/complete-processing']['body']) =>
      fetch(`articles/${articleId}/complete-processing`, undefined, { method: 'POST', body }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
      await queryClient.invalidateQueries({
        queryKey: ['articles', articleId, 'condition-form', 'quality_control'],
      });
    },
  });
};

const invalidateBulkArticleQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['requests'] });
  queryClient.invalidateQueries({ queryKey: ['activities'] });
  queryClient.invalidateQueries({ queryKey: ['notifications'] });
};

export const useBulkValidateTransit = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/bulk-validate-transit']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/bulk-validate-transit']['body']) =>
      fetch('/articles/bulk-validate-transit', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkArticleQueries(queryClient);
    },
  });
};

export const useBulkStartRepair = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/bulk-start-repair']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/bulk-start-repair']['body']) =>
      fetch('/articles/bulk-start-repair', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkArticleQueries(queryClient);
    },
  });
};

export const useBulkStartExchange = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/bulk-start-exchange']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/bulk-start-exchange']['body']) =>
      fetch('/articles/bulk-start-exchange', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkArticleQueries(queryClient);
    },
  });
};

export const useBulkRepair = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/bulk-repair']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/bulk-repair']['body']) =>
      fetch('/articles/bulk-repair', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkArticleQueries(queryClient);
    },
  });
};

export const useBulkConfirmProcessingReception = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/bulk-confirm-processing-reception']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/bulk-confirm-processing-reception']['body']) =>
      fetch('/articles/bulk-confirm-processing-reception', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkArticleQueries(queryClient);
    },
  });
};

export const useCancelProcessing = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/cancel-processing']>();

  return useMutation({
    mutationFn: () =>
      fetch(`articles/${articleId}/cancel-processing`, undefined, { method: 'POST' }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const WARRANTY_OVERRIDE_REASONS: {
  id: 'commercial-gesture' | 'warranty-verified-manually';
  text: MessageDescriptor;
}[] = [
  {
    id: 'commercial-gesture',
    text: msg({
      id: 'article.form.warranty-override-toggle.override-reason.commercial-gesture',
      message: 'Commercial gesture',
    }),
  },
  {
    id: 'warranty-verified-manually',
    text: msg({
      id: 'article.form.warranty-override-toggle.override-reason.warranty-verified-manually',
      message: 'Warranty verified manually',
    }),
  },
];

export const ARTICLE_TASK_TYPES = [
  {
    id: 'accept_dispatch',
    label: msg({
      id: 'article.task.type.accept_dispatch',
      message: 'Dispatch (Job review)',
    }),
    permissions: ['accept_dispatch', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'accept_dispatch_proposal',
    label: msg({
      id: 'article.task.type.accept_dispatch_proposal',
      message: 'Quote review',
    }),
    permissions: ['choose_article_service'],
  },
  {
    id: 'accept_requalification',
    label: msg({
      id: 'article.task.type.accept_requalification',
      message: 'New quote analysis',
    }),
    permissions: ['accept_requalification'],
  },
  {
    id: 'add_customs_informations',
    label: msg({
      id: 'article.task.type.add_customs_informations',
      message: 'Customs information',
    }),
    permissions: ['edit_customs_data'],
  },
  {
    id: 'adjust_service_during_quality_control',
    label: msg({
      id: 'article.task.type.adjust_service_during_quality_control',
      message: 'Quality control (service adjustment)',
    }),
    permissions: ['control_quality'],
  },
  {
    id: 'adjust_service_during_processing',
    label: msg({
      id: 'article.task.type.adjust_service_during_processing',
      message: 'Processing (service adjustment)',
    }),
    permissions: ['verify_shipment_reception'],
  },
  {
    id: 'analyze_article',
    label: msg({ id: 'article.task.type.analyze_article', message: 'Analysis' }),
    permissions: ['analyze_article', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'choose_article_service',
    label: msg({ id: 'article.task.type.choose_article_service', message: 'Diagnosis' }),
    permissions: ['choose_article_service'],
  },
  {
    id: 'confirm_exchange_stock_availability',
    label: msg({
      id: 'article.task.type.confirm_exchange_stock_availability',
      message: 'Stock availability confirmation',
    }),
    permissions: [
      'confirm_exchange_stock_availability',
      'act_on_behalf_of_non_digitalized_workshop',
    ],
  },
  {
    id: 'contact_client',
    label: msg({
      id: 'article.task.type.contact_client',
      message: 'Client outreach',
    }),
    permissions: ['write_client_communication'],
  },
  {
    id: 'control_quality',
    label: msg({
      id: 'article.task.type.control_quality',
      message: 'Quality control',
    }),
    permissions: ['control_quality'],
  },
  {
    id: 'create_shipment',
    label: msg({ id: 'article.task.type.create_shipment', message: 'Shipment creation' }),
    permissions: ['create_shipment_from_origin', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'dropoff_shipment',
    label: msg({ id: 'article.task.type.dropoff_shipment', message: 'Shipment dropoff' }),
    permissions: ['create_shipment_from_origin', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'exchange_article',
    label: msg({ id: 'article.task.type.exchange_article', message: 'Exchange' }),
    permissions: ['exchange_article', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'start_article_exchange',
    label: msg({ id: 'article.task.type.start_article_exchange', message: 'Start exchange' }),
    permissions: ['exchange_article', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'finalise_shipment',
    label: msg({
      id: 'article.task.type.finalise_shipment',
      message: 'Shipment finalisation',
    }),
    permissions: ['create_shipment_from_origin', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'handle_shipment_issue',
    label: msg({
      id: 'article.task.type.handle_shipment_issue',
      message: 'Shipment issue',
    }),
    labelVariant: 'danger',
    permissions: [
      'verify_shipment_reception',
      'create_shipment_from_origin',
      'act_on_behalf_of_non_digitalized_workshop',
      'view_all_shipments_issues',
    ],
  },
  {
    id: 'prepare_shipment',
    label: msg({
      id: 'article.task.type.prepare_shipment',
      message: 'Shipment preparation',
    }),
    permissions: ['create_shipment_from_origin', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'repair_article',
    label: msg({ id: 'article.task.type.repair_article', message: 'Repair completion' }),
    permissions: ['repair_article', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'start_article_repair',
    label: msg({ id: 'article.task.type.start_article_repair', message: 'Repair start' }),
    permissions: ['repair_article', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'validate_payment',
    label: msg({
      id: 'article.task.type.validate_payment',
      message: 'Payment',
    }),
    permissions: ['validate_payment'],
  },
  {
    id: 'validate_refund',
    label: msg({
      id: 'article.task.type.validate_refund',
      message: 'Refund',
    }),
    permissions: ['validate_payment'],
  },
  {
    id: 'validate_transit',
    label: msg({ id: 'article.task.type.validate_transit', message: 'Transit validation' }),
    permissions: ['create_shipment_from_origin', 'act_on_behalf_of_non_digitalized_workshop'],
  },
  {
    id: 'verify_shipment_reception',
    label: msg({
      id: 'article.task.type.verify_shipment_reception',
      message: 'Shipment verification',
    }),
    permissions: ['verify_shipment_reception'],
  },
] satisfies {
  id: ArticleTaskType;
  label: MessageDescriptor;
  labelVariant?: 'danger';
  permissions: Permission[];
}[];

export const JOB_REFUSED_REASONS = [
  {
    id: 'item-too-damaged',
    label: msg({
      id: 'article.job-refused.item-too-damaged',
      message: 'The item is too damaged to be handled',
    }),
    onBehalfLabel: msg({
      id: 'article.job-refused.item-too-damaged',
      message: 'The item is too damaged to be handled',
    }),
    requireComment: false,
    disableCancellationFees: false,
  },
  {
    id: 'actions-wrongly-specified',
    label: msg({
      id: 'article.job-refused.actions-wrongly-specified',
      message: "Actions were wrongly specified and I can't handle them",
    }),
    onBehalfLabel: msg({
      id: 'article.job-refused.actions-wrongly-specified.on-behalf',
      message: "The actions were wrongly specified and the workshop can't handle them",
    }),
    requireComment: true,
    disableCancellationFees: false,
  },
  {
    id: 'not-enough-time',
    label: msg({
      id: 'article.job-refused.not-enough-time',
      message: "I don't have enough time/resources to handle the item",
    }),
    onBehalfLabel: msg({
      id: 'article.job-refused.not-enough-time.on-behalf',
      message: "The workshop doesn't have enough time/resources to handle the item",
    }),
    requireComment: false,
    disableCancellationFees: true,
  },
  {
    id: 'custom',
    label: msg({
      id: 'article.job-refused.custom',
      message: 'Other reason, please specify',
    }),
    onBehalfLabel: msg({
      id: 'article.job-refused.custom',
      message: 'Other reason, please specify',
    }),
    requireComment: true,
    disableCancellationFees: false,
  },
] satisfies {
  id: ActivityOfType<'job_refused'>['data']['reason'];
  label: MessageDescriptor;
  onBehalfLabel: MessageDescriptor;
  requireComment: boolean;
  disableCancellationFees: boolean;
}[];

export type JobRefusedReason = (typeof JOB_REFUSED_REASONS)[number]['id'];

export const EXCEPTIONAL_BRAND_COVERAGE_REASONS = [
  {
    id: 'recurring-malfunction',
    text: msg({
      id: 'defect-card.exceptional-coverage.reason.recurring-malfunction',
      message: 'Recurring Malfunction',
    }),
  },
  {
    id: 'brand-recall-campaign',
    text: msg({
      id: 'defect-card.exceptional-coverage.reason.brand-recall-campaign',
      message: 'Brand Recall Campaign',
    }),
  },
  {
    id: 'suspected-manufacturing-defect',
    text: msg({
      id: 'defect-card.exceptional-coverage.reason.suspected-manufacturing-defect',
      message: 'Suspected Manufacturing Defect',
    }),
  },
  {
    id: 'covered-by-workshop-warranty',
    text: msg({
      id: 'defect-card.exceptional-coverage.reason.covered-by-workshop-warranty',
      message: 'Covered by Workshop Warranty',
    }),
  },
] satisfies {
  id: ExceptionalBrandCoverageReason;
  text: MessageDescriptor;
}[];

export type ConditionFormData = Endpoints['GET /articles/:articleId/condition-form']['response'];
export type ConditionForm = NonNullable<ConditionFormData>['conditionForm'];
export type ArticleConditionForm = NonNullable<ConditionFormData>['articleConditionForms'][number];

type ConditionFormType = 'initial_condition' | 'quality_control';

export const useArticleConditionForm = (
  { articleId, type = 'initial_condition' }: { articleId?: string; type?: ConditionFormType },
  options?: {
    enabled?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /articles/:articleId/condition-form']>();

  return useQuery({
    queryKey: ['articles', articleId, 'condition-form', type],
    queryFn: () => fetch(`/articles/${articleId}/condition-form`, { type }),
    enabled: options?.enabled,
  });
};

export const useArticlesConditionForms = (
  { articleIds, type = 'initial_condition' }: { articleIds: string[]; type?: ConditionFormType },
  options?: {
    enabled?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /articles/:articleId/condition-form']>();

  return useQueries({
    queries: articleIds.map((articleId) => ({
      queryKey: ['articles', articleId, 'condition-form', type],
      queryFn: () => fetch(`/articles/${articleId}/condition-form`, { type }),
      enabled: options?.enabled,
    })),
  });
};

export const useUpdateArticleCondition = ({
  requestId,
  articleId,
  conditionForm,
}: {
  requestId: string;
  articleId: string;
  conditionForm?: {
    id: string;
    type: 'initial_condition' | 'quality_control';
  };
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:articleId/condition-form/:conditionFormId']>();
  const articleConditionFormQueryKey = [
    'articles',
    articleId,
    'condition-form',
    conditionForm?.type,
  ];

  return useMutation({
    mutationFn: (
      body: Endpoints['PATCH /articles/:articleId/condition-form/:conditionFormId']['body']
    ) => {
      // Optimistic update
      const currentData = queryClient.getQueryData<
        Endpoints['GET /articles/:articleId/condition-form']['response']
      >(articleConditionFormQueryKey);

      if (currentData) {
        const editableArticleConditionForm = currentData.articleConditionForms.find(
          (articleConditionForm) => !articleConditionForm.completedAt
        )!;

        let updatedArticleConditions = editableArticleConditionForm.articleConditions;

        if ((body.questionId || body.articleActionId) && body.answers) {
          const newConditions = body.answers.map((answer) => ({
            id: `${TEMPORARY_ID_PREFIX}${Date.now()}`,
            questionId: body.questionId ?? null,
            articleActionId: body.articleActionId ?? null,
            articleAction: null,
            answerId: answer.id ?? null,
            answerData: answer.data ?? null,
            context: body.context ?? null,
            media: [],
          }));

          updatedArticleConditions = [
            ...editableArticleConditionForm.articleConditions.filter((articleCondition) =>
              body.questionId
                ? articleCondition.questionId !== body.questionId
                : articleCondition.articleActionId !== body.articleActionId
            ),
            ...newConditions,
          ];
        }

        const updatedArticleConditionForm = {
          ...editableArticleConditionForm,
          articleConditions: updatedArticleConditions,
          answerer: body.answerer ?? editableArticleConditionForm.answerer,
        };

        const updatedData: Endpoints['GET /articles/:articleId/condition-form']['response'] = {
          ...currentData,
          articleConditionForms: [
            updatedArticleConditionForm,
            ...currentData.articleConditionForms.filter(
              (articleConditionForm) => !!articleConditionForm.completedAt
            ),
          ],
        };

        queryClient.setQueryData(articleConditionFormQueryKey, updatedData);
      }

      return fetch(`/articles/${articleId}/condition-form/${conditionForm?.id}`, undefined, {
        method: 'PATCH',
        body,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: articleConditionFormQueryKey });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
};

export const useUpdateManualRepairDueDays = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/manual-repair-due-days']>();

  return useMutation({
    mutationFn: ({ ...body }: Endpoints['POST /articles/:id/manual-repair-due-days']['body']) =>
      fetch(`/articles/${articleId}/manual-repair-due-days`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useUpdateManualExchangeDueDays = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/manual-exchange-due-days']>();

  return useMutation({
    mutationFn: ({ ...body }: Endpoints['POST /articles/:id/manual-exchange-due-days']['body']) =>
      fetch(`/articles/${articleId}/manual-exchange-due-days`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useSetManualStatusDueAt = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /articles/:id/status-due-at']>();

  return useMutation({
    mutationFn: (body: Endpoints['PATCH /articles/:id/status-due-at']['body']) =>
      fetch(`/articles/${articleId}/status-due-at`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useRecordClientContact = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/record-client-contact']>();

  return useMutation({
    mutationFn: ({ ...body }: Endpoints['POST /articles/:id/record-client-contact']['body']) =>
      fetch(`/articles/${articleId}/record-client-contact`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useSignArticlePickUpReceipt = ({
  requestId,
  articleId,
}: {
  requestId: string;
  articleId: string | undefined;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/sign-pick-up-receipt']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/sign-pick-up-receipt']['body']) =>
      fetch(`/articles/${articleId}/sign-pick-up-receipt`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId, 'documents'] });
    },
  });
};

export const useConfirmExchange = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/confirm-exchange']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/confirm-exchange`, undefined, { method: 'POST' }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useConfirmExchangeStockAvailability = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/confirm-exchange-stock-availability']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/confirm-exchange-stock-availability`, undefined, {
        method: 'POST',
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useRefuseExchangeStockAvailability = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/refuse-exchange-stock-availability']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/articles/${articleId}/refuse-exchange-stock-availability`, undefined, {
        method: 'POST',
      }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useCancelExchange = ({ articleId }: { articleId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /articles/:id/cancel-exchange']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /articles/:id/cancel-exchange']['body']) =>
      fetch(`/articles/${articleId}/cancel-exchange`, undefined, { method: 'POST', body }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

const stepIsClientPickUp = (step: ArticleSteps[number]): boolean =>
  step.step === 'transit' &&
  'clientPickup' in step.config &&
  !!step.config.clientPickup &&
  'originType' in step.config &&
  step.config.originType === 'store' &&
  'destinationType' in step.config &&
  step.config.destinationType === 'client';

export const articleIsReadyForPickup = (article: ArticleWithRelations): boolean => {
  if (!article.steps) return false;

  const pickUpStep = article.steps.find(stepIsClientPickUp);

  if (!pickUpStep) return false;

  return (
    pickUpStep.status === 'in-progress' &&
    // Don't consider the article if there is a pending client contact task
    !article.taskType
  );
};

export type ArticleConditionFormType = 'initial-condition' | 'quality-control';

export const getArticleCurrency = (
  article: ArticleWithRelations,
  type: 'organization' | 'workshop'
): Currency | null => {
  const currencyFromPriceAggregate =
    type === 'organization'
      ? article.snapshot.organizationPrice?.amountPerCurrency?.[0]?.currency
      : article.snapshot.workshopPrice?.amountPerCurrency?.[0]?.currency;

  if (currencyFromPriceAggregate) {
    return currencyFromPriceAggregate;
  }

  const currencyFromActions = article.snapshot.articleActions
    .map((action) =>
      type === 'organization'
        ? action.customOrganizationPrice?.currency
        : action.customWorkshopPrice?.currency
    )
    .find((customPrice) => !!customPrice);

  if (currencyFromActions) {
    return currencyFromActions;
  }

  const currencyFromExchange =
    type === 'organization'
      ? article.snapshot.articleExchange?.customOrganizationPrice?.currency
      : article.snapshot.articleExchange?.customWorkshopPrice?.currency;

  if (currencyFromExchange) {
    return currencyFromExchange;
  }

  return null;
};
